import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { GroupedBundleLeaf } from "@/pages/bundles/types/grouped-bundle";
import { useChangeBundlePrice } from "../hooks/use-change-bundle-price";
import { usePartialReleaseBundle } from "../hooks/use-pricing-lifecycle";

type Mode = "SET" | "INCREASE" | "DECREASE" | "ROLLBACK";
type ValueType = "AMOUNT" | "PERCENT";
// How the computed price is applied: NOW = instant change for everyone (new
// baseline); PARTIAL = experimental release visible only to cohorts entering
// on/after the release date.
type ApplyMode = "NOW" | "PARTIAL";

// Local datetime → "yyyy-MM-ddThh:mm" for the <input type="datetime-local">.
function toLocalInput(d: Date): string {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

// The earliest a new partial release may be scheduled: now, or one minute after
// the latest existing release (releases must be strictly increasing). Hides
// past/equal dates from the picker so the server's ordering rule can't be hit.
function earliestRelease(latestReleaseAt?: string | null): Date {
  const now = new Date();
  now.setSeconds(0, 0);
  if (latestReleaseAt) {
    const latest = new Date(latestReleaseAt);
    const afterLatest = new Date(latest.getTime() + 60_000);
    if (afterLatest > now) return afterLatest;
  }
  return now;
}

function preview(current: number, mode: Mode, valueType: ValueType, value: number) {
  if (Number.isNaN(value)) return current;
  if (mode === "SET") return Math.round(value);
  const sign = mode === "DECREASE" ? -1 : 1;
  if (valueType === "PERCENT") return Math.round(current * (1 + (sign * value) / 100));
  return Math.round(current + sign * value);
}

export default function PriceChangeDialog({
  bundle,
  history,
  initialMode = "SET",
  latestReleaseAt,
  onClose,
}: {
  // The offering's current ACTIVE bundle.
  bundle: GroupedBundleLeaf | null;
  // Past prices (newest → oldest) — for the roll-back picker.
  history: GroupedBundleLeaf[];
  initialMode?: Mode;
  // Latest existing experimental release date (ISO) for this offering — a new
  // partial release must be scheduled AFTER it (the picker floors to it).
  latestReleaseAt?: string | null;
  onClose: () => void;
}) {
  const open = !!bundle;
  const change = useChangeBundlePrice();
  const partial = usePartialReleaseBundle();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [valueType, setValueType] = useState<ValueType>("AMOUNT");
  const [value, setValue] = useState<string>("");
  const [rollbackId, setRollbackId] = useState<string>("");
  const [applyMode, setApplyMode] = useState<ApplyMode>("NOW");
  const [releasedAt, setReleasedAt] = useState<string>("");
  // The new version's per-session price (RH-1006). Prefilled with the current
  // one; per-session price is per version, so a new price usually needs a new
  // figure here too.
  const [perSessionValue, setPerSessionValue] = useState<string>("");

  // Reset to a clean state each time the dialog opens.
  useEffect(() => {
    if (bundle) {
      setMode(initialMode);
      setValueType("AMOUNT");
      setValue("");
      setRollbackId("");
      setApplyMode("NOW");
      setReleasedAt(toLocalInput(earliestRelease(latestReleaseAt)));
      setPerSessionValue(
        bundle.single_session_amount != null
          ? String(bundle.single_session_amount)
          : "",
      );
    }
  }, [bundle, initialMode, latestReleaseAt]);

  const minReleaseLocal = toLocalInput(earliestRelease(latestReleaseAt));

  // Roll back always applies to everyone (restore a past baseline) — partial
  // release is meaningless there.
  const partialAllowed = mode !== "ROLLBACK";
  const isPartial = applyMode === "PARTIAL" && partialAllowed;

  const current = bundle?.amount ?? 0;
  const numericValue = parseFloat(value);
  const rollbackAmount =
    history.find((h) => h.id === rollbackId)?.amount ?? null;

  const next = useMemo(() => {
    if (mode === "ROLLBACK") return rollbackAmount ?? current;
    return preview(current, mode, valueType, numericValue);
  }, [mode, rollbackAmount, current, valueType, numericValue]);

  const valid =
    mode === "ROLLBACK"
      ? rollbackAmount !== null && rollbackAmount !== current
      : !Number.isNaN(numericValue) &&
        numericValue >= 0 &&
        next >= 0 &&
        next !== current;

  // Partial release only needs a positive resulting price + a release date (it
  // may equal the current baseline — it's a new-cohort experiment, not a diff).
  const validPartial =
    !Number.isNaN(next) &&
    next > 0 &&
    !!releasedAt &&
    new Date(releasedAt) >= earliestRelease(latestReleaseAt) &&
    mode !== "ROLLBACK";
  const perSessionNumber =
    perSessionValue.trim() === "" ? undefined : Number(perSessionValue);
  const perSessionValid =
    perSessionNumber === undefined ||
    (Number.isInteger(perSessionNumber) && perSessionNumber >= 0);
  // Only send it when it changes the version's figure (else it carries over).
  const perSessionPayload =
    mode !== "ROLLBACK" &&
    perSessionNumber !== undefined &&
    perSessionNumber !== bundle?.single_session_amount
      ? { single_session_amount: perSessionNumber }
      : {};
  const canSubmit = (isPartial ? validPartial : valid) && perSessionValid;
  const busy = change.isPending || partial.isPending;

  const submit = () => {
    if (!bundle) return;

    if (isPartial) {
      partial.mutate(
        {
          baselineId: bundle.id,
          payload: {
            amount: next,
            released_at: new Date(releasedAt).toISOString(),
            ...perSessionPayload,
          },
        },
        {
          onSuccess: () => {
            toast({
              title: "Partial release created",
              description:
                "New cohorts (from the release date) will see this price; existing users keep theirs.",
            });
            onClose();
          },
          onError: (e: any) =>
            toast({
              title: "Error",
              description:
                e.response?.data?.message ||
                "Failed to create partial release",
              variant: "destructive",
            }),
        },
      );
      return;
    }

    // Apply now — instant change for everyone (new baseline). Roll back = set the
    // price to the chosen past amount.
    const payload =
      mode === "ROLLBACK"
        ? { mode: "SET" as const, value: rollbackAmount ?? current }
        : {
            mode,
            valueType: mode === "SET" ? undefined : valueType,
            value: numericValue,
            ...perSessionPayload,
          };
    change.mutate(
      { id: bundle.id, payload },
      {
        onSuccess: () => {
          toast({
            title: mode === "ROLLBACK" ? "Price rolled back" : "Price updated",
            description: "The old price moved to history.",
          });
          onClose();
        },
        onError: (e: any) =>
          toast({
            title: "Error",
            description: e.response?.data?.message || "Failed to change price",
            variant: "destructive",
          }),
      },
    );
  };

  if (!bundle) return null;
  const fmt = (n: number) => `${bundle.currency} ${n.toLocaleString()}`;
  const sessions = bundle.session_quantity ?? 0;
  const perSession = (n: number) =>
    sessions > 0 ? ` · ${fmt(Math.round(n / sessions))}/session` : "";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Change price — {bundle.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <p className="text-xs text-gray-500 mb-1">Mode</p>
              <Select value={mode} onValueChange={(v) => setMode(v as Mode)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SET">Set to</SelectItem>
                  <SelectItem value="INCREASE">Increase by</SelectItem>
                  <SelectItem value="DECREASE">Decrease by</SelectItem>
                  {history.length > 0 && (
                    <SelectItem value="ROLLBACK">Roll back to</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            {mode === "INCREASE" || mode === "DECREASE" ? (
              <div className="w-32">
                <p className="text-xs text-gray-500 mb-1">By</p>
                <Select
                  value={valueType}
                  onValueChange={(v) => setValueType(v as ValueType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="AMOUNT">Amount</SelectItem>
                    <SelectItem value="PERCENT">Percent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </div>

          {mode === "ROLLBACK" ? (
            <div>
              <p className="text-xs text-gray-500 mb-1">Past price</p>
              <Select value={rollbackId} onValueChange={setRollbackId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a past price" />
                </SelectTrigger>
                <SelectContent>
                  {history.map((h) => (
                    <SelectItem key={h.id} value={h.id}>
                      {fmt(h.amount)} · {format(new Date(h.created_at), "dd MMM yyyy")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div>
              <p className="text-xs text-gray-500 mb-1">
                {mode === "SET"
                  ? "New amount"
                  : valueType === "PERCENT"
                    ? "Percent"
                    : "Amount"}
              </p>
              <Input
                type="number"
                min={0}
                placeholder={mode === "SET" ? "New amount" : "e.g. 10"}
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            </div>
          )}

          <div className="rounded-md border border-gray-200 p-3 text-sm flex items-center justify-between">
            <span className="text-gray-500">
              {fmt(current)}
              <span className="text-gray-400">{perSession(current)}</span>
            </span>
            <span className="text-gray-400">→</span>
            <span className="font-semibold">
              {valid ? (
                <>
                  {fmt(next)}
                  <span className="font-normal text-gray-400">
                    {perSession(next)}
                  </span>
                </>
              ) : (
                "—"
              )}
            </span>
          </div>

          {mode !== "ROLLBACK" && (
            <div>
              <p className="text-xs text-gray-500 mb-1">
                Per-session price for the new version
              </p>
              <Input
                type="number"
                min={0}
                placeholder="Not set"
                value={perSessionValue}
                onChange={(e) => setPerSessionValue(e.target.value)}
              />
              <p className="text-[11px] text-gray-400 mt-1">
                Quoted by refund copy ({"{{PER_SESSION_PRICE}}"}). Each price
                version keeps its own.
                {valid && sessions > 0 && (
                  <>
                    {" "}
                    <button
                      type="button"
                      className="underline"
                      onClick={() =>
                        setPerSessionValue(String(Math.round(next / sessions)))
                      }
                    >
                      Use {fmt(Math.round(next / sessions))}
                    </button>{" "}
                    (new amount ÷ {sessions} sessions)
                  </>
                )}
              </p>
            </div>
          )}

          {/* How to apply the computed price. Roll back is always "now". */}
          {partialAllowed && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Apply</p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={applyMode === "NOW" ? "default" : "outline"}
                  size="sm"
                  className="flex-1"
                  onClick={() => setApplyMode("NOW")}
                >
                  Now — everyone
                </Button>
                <Button
                  type="button"
                  variant={applyMode === "PARTIAL" ? "default" : "outline"}
                  size="sm"
                  className="flex-1"
                  onClick={() => setApplyMode("PARTIAL")}
                >
                  Partial release
                </Button>
              </div>
              <p className="text-[11px] text-gray-400 mt-1">
                {isPartial
                  ? "Only users entering on/after the release date get this price; existing users keep theirs."
                  : "Immediately becomes the price for everyone (new + existing)."}
              </p>
            </div>
          )}

          {isPartial && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Release date &amp; time</p>
              <Input
                type="datetime-local"
                min={minReleaseLocal}
                value={releasedAt}
                onChange={(e) => setReleasedAt(e.target.value)}
              />
              {latestReleaseAt && (
                <p className="text-[11px] text-gray-400 mt-1">
                  Must be after the latest release (
                  {format(new Date(latestReleaseAt), "dd MMM yyyy HH:mm")}).
                </p>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!canSubmit || busy} onClick={submit}>
              {busy
                ? "Saving…"
                : isPartial
                  ? "Release"
                  : mode === "ROLLBACK"
                    ? "Roll back"
                    : "Apply now"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
